"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TournamentClient = void 0;
/**
 * Tournament Client
 *
 * Client for interacting with Tournament API from frontend applications
 */
const axios_1 = __importDefault(require("axios"));
class TournamentClient {
    constructor(config) {
        this.tokenCache = null;
        this.lastRequestTime = 0;
        this.MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests
        this.config = config;
        this.api = axios_1.default.create({
            baseURL: config.apiUrl,
        });
        // Add request interceptor to include JWT token with throttling
        this.api.interceptors.request.use(async (config) => {
            // Throttle requests - ensure minimum interval between requests
            const now = Date.now();
            const timeSinceLastRequest = now - this.lastRequestTime;
            if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
                await new Promise(resolve => setTimeout(resolve, this.MIN_REQUEST_INTERVAL - timeSinceLastRequest));
            }
            this.lastRequestTime = Date.now();
            console.log('[TournamentClient] Preparing request:', {
                url: config.url,
                method: config.method,
                hasGetAccessToken: !!this.config.getAccessToken,
                hasCachedToken: !!this.tokenCache,
                cachedTokenExpired: this.tokenCache ? this.tokenCache.expiresAt <= now : true,
            });
            if (this.config.getAccessToken) {
                try {
                    // Cache token for 30 seconds to reduce calls
                    const cacheNow = Date.now();
                    if (this.tokenCache && this.tokenCache.expiresAt > cacheNow) {
                        if (this.tokenCache.token) {
                            console.log('[TournamentClient] Using cached token');
                            config.headers.Authorization = `Bearer ${this.tokenCache.token}`;
                        }
                        else {
                            console.warn('[TournamentClient] Cached token is null');
                        }
                        return config;
                    }
                    console.log('[TournamentClient] Fetching new token...');
                    const token = await this.config.getAccessToken();
                    console.log('[TournamentClient] Token retrieved:', {
                        hasToken: !!token,
                        tokenLength: token?.length || 0,
                        tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
                    });
                    // Cache token for 30 seconds
                    this.tokenCache = {
                        token,
                        expiresAt: cacheNow + 30000, // 30 seconds
                    };
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                        console.log('[TournamentClient] ✅ Authorization header set');
                    }
                    else {
                        console.error('[TournamentClient] ❌ getAccessToken returned null/undefined - request will fail');
                        console.error('[TournamentClient] Request config:', {
                            url: config.url,
                            method: config.method,
                            headers: Object.keys(config.headers || {}),
                        });
                    }
                }
                catch (error) {
                    console.error('[TournamentClient] ❌ Error getting access token:', error);
                    console.error('[TournamentClient] Error details:', {
                        message: error?.message,
                        stack: error?.stack,
                        name: error?.name,
                    });
                    // Clear cache on error
                    this.tokenCache = null;
                }
            }
            else {
                console.error('[TournamentClient] ❌ No getAccessToken function provided in config');
            }
            const authHeader = config.headers?.Authorization;
            const authHeaderStr = typeof authHeader === 'string' ? authHeader : String(authHeader || '');
            console.log('[TournamentClient] Final request config:', {
                url: config.url,
                method: config.method,
                hasAuthHeader: !!authHeader,
                authHeaderPrefix: authHeaderStr ? authHeaderStr.substring(0, 20) + '...' : 'none',
            });
            return config;
        });
        // Add response interceptor to handle 429 errors
        this.api.interceptors.response.use((response) => response, async (error) => {
            if (error?.response?.status === 429) {
                // Rate limited - wait and retry once
                const retryAfter = error.response.headers['retry-after']
                    ? parseInt(error.response.headers['retry-after']) * 1000
                    : 5000; // Default 5 seconds
                console.warn(`[TournamentClient] Rate limited (429), waiting ${retryAfter}ms before retry`);
                await new Promise(resolve => setTimeout(resolve, retryAfter));
                // Clear token cache to force fresh token
                this.tokenCache = null;
                // Retry the request
                return this.api.request(error.config);
            }
            return Promise.reject(error);
        });
    }
    // Tournament methods
    async getTournaments() {
        const response = await this.api.get('/tournaments');
        return response.data;
    }
    async getTournament(id) {
        const response = await this.api.get(`/tournaments/${id}`);
        return response.data;
    }
    async createTournament(name, entryFee, ticketPrice) {
        const response = await this.api.post('/tournaments', { name, entryFee, ticketPrice });
        return response.data;
    }
    async depositToTournament(tournamentId, userAddress, amount) {
        const response = await this.api.post(`/tournaments/${tournamentId}/deposit`, {
            userAddress,
            amount,
        });
        return response.data;
    }
    async startTournament(tournamentId) {
        const response = await this.api.post(`/tournaments/${tournamentId}/start`, {});
        return response.data;
    }
    async determineWinners(tournamentId, winners) {
        const response = await this.api.post(`/tournaments/${tournamentId}/winners`, { winners });
        return response.data;
    }
    async withdrawFromTournament(tournamentId, userAddress) {
        const response = await this.api.post(`/tournaments/${tournamentId}/withdraw`, { userAddress });
        return response.data;
    }
    async closeTournament(tournamentId) {
        const response = await this.api.post(`/tournaments/${tournamentId}/close`, {});
        return response.data;
    }
}
exports.TournamentClient = TournamentClient;
